// Miky Motorsport - trat (M2): vizual + racing line + checkpointy + grid + progress
import * as THREE from 'three';
import { RAPIER } from './physics/world.js';
import { buildScenery } from './render/scenery.js';
import { getTrackDef, getBiome } from './data/tracks.js';
import { catmull, elevBase } from './trackMath.js';

const ROAD_W = 14;
const SEG = 24; // samplu na segment splajnu
const CHECKPOINT_EVERY = 12; // checkpoint kazdych N samplu

// def: track definice z data/tracks.js (id, name, ctrl, laps). Default = apex_ring.
export function buildTrack(world, scene, def) {
  def = def || getTrackDef();
  const CTRL = def.ctrl;
  const LAPS = def.laps || 3;
  const SAMPLES = CTRL.length * SEG;
  const meshes = [];
  const bodies = []; // fyzikalni telesa (pro dispose pri prepnuti trati)

  // --- Centerline (racing line) z control pointu pres Catmull-Rom ---
  const pts = []; // {x,z}
  const nc = CTRL.length;
  for (let i = 0; i < nc; i++) {
    const p0 = CTRL[(i - 1 + nc) % nc], p1 = CTRL[i], p2 = CTRL[(i + 1) % nc], p3 = CTRL[(i + 2) % nc];
    for (let s = 0; s < SEG; s++) {
      const t = s / SEG;
      pts.push({ x: catmull(p0[0], p1[0], p2[0], p3[0], t), z: catmull(p0[1], p1[1], p2[1], p3[1], t) });
    }
  }
  // kumulativni delka oblouku
  const cum = new Array(SAMPLES + 1);
  cum[0] = 0;
  for (let i = 0; i < SAMPLES; i++) {
    const a = pts[i];
    const b = pts[(i + 1) % SAMPLES];
    cum[i + 1] = cum[i] + Math.hypot(b.x - a.x, b.z - a.z);
  }
  const totalLength = cum[SAMPLES];

  // Elevace (kopce): biom skaluje amplitudu (mesto plossi). E(x,z) = vyska terenu.
  const biome = getBiome(def.biome);
  const elevAmp = biome.elevAmp ?? 1.0;
  const E = (x, z) => elevBase(x, z) * elevAmp;

  function tangentAt(i) {
    const a = pts[(i - 1 + SAMPLES) % SAMPLES];
    const b = pts[(i + 1) % SAMPLES];
    let tx = b.x - a.x;
    let tz = b.z - a.z;
    const l = Math.hypot(tx, tz) || 1;
    return { x: tx / l, z: tz / l };
  }
  function normalAt(i) {
    const t = tangentAt(i);
    return { x: t.z, z: -t.x }; // kolmice (vpravo od smeru)
  }

  // --- Checkpointy ---
  const checkpoints = [];
  for (let i = 0; i < SAMPLES; i += CHECKPOINT_EVERY) {
    const p = pts[i];
    const t = tangentAt(i);
    const n = normalAt(i);
    checkpoints.push({
      x: p.x, z: p.z, tx: t.x, tz: t.z, nx: n.x, nz: n.z,
      halfWidth: ROAD_W * 0.55, s: cum[i], idx: checkpoints.length,
    });
  }
  const numCheckpoints = checkpoints.length;

  // --- Grid pozice (za startovni carou cp0) ---
  const gridPositions = [];
  const GRID = 6;
  for (let j = 0; j < GRID; j++) {
    const back = 7 + j * 5.5; // metry za startem
    const s = (totalLength - back + totalLength) % totalLength;
    const gp = pointAtS(s);
    const lat = (j % 2 === 0 ? -1 : 1) * 3.0;
    const gx = gp.x + gp.nx * lat, gz = gp.z + gp.nz * lat;
    gridPositions.push({
      pos: { x: gx, y: E(gx, gz) + 1.05, z: gz },
      heading: Math.atan2(gp.tx, gp.tz),
    });
  }

  function pointAtS(s) {
    s = ((s % totalLength) + totalLength) % totalLength;
    // najdi segment
    let lo = 0, hi = SAMPLES;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (cum[mid] <= s) lo = mid + 1; else hi = mid;
    }
    const i = Math.max(0, lo - 1);
    const seg = cum[i + 1] - cum[i] || 1;
    const f = (s - cum[i]) / seg;
    const a = pts[i];
    const b = pts[(i + 1) % SAMPLES];
    const t = tangentAt(i);
    const n = normalAt(i);
    const px = a.x + (b.x - a.x) * f, pz = a.z + (b.z - a.z) * f;
    return { x: px, z: pz, y: E(px, pz), tx: t.x, tz: t.z, nx: n.x, nz: n.z };
  }

  // Nejblizsi bod na centerline -> {idx, s, lateral}.
  // [F-430] s je SPOJITA arc-length: nejprve najdi nejblizsi SAMPLE (bi), pak query bod
  // promitni na oba sousedni segmenty ([bi-1,bi] a [bi,bi+1]) a vrat s podle frakce projekce.
  // Drive se vracelo s = cum[bi] (kvantovane na sample) -> mnoho pozic remizovalo a razeni/
  // tie-breaky eliminace byly nestabilni.
  function nearest(x, z) {
    let best = Infinity, bi = 0;
    for (let i = 0; i < SAMPLES; i++) {
      const dx = pts[i].x - x, dz = pts[i].z - z;
      const d = dx * dx + dz * dz;
      if (d < best) { best = d; bi = i; }
    }
    // Promitni na oba segmenty kolem bi, vyber blizsi (spojita s + spravny lateral).
    const segPrev = (bi - 1 + SAMPLES) % SAMPLES; // segment [segPrev, bi]
    let bestS = cum[bi];
    let bestLateral = 0;
    let bestDist = Infinity;
    for (const segIdx of [segPrev, bi]) {
      const a = pts[segIdx];
      const b = pts[(segIdx + 1) % SAMPLES];
      const ex = b.x - a.x, ez = b.z - a.z;
      const segLen2 = ex * ex + ez * ez || 1;
      // frakce projekce query bodu na segment, orezana na [0,1] (drzi se v ramci segmentu)
      let t = ((x - a.x) * ex + (z - a.z) * ez) / segLen2;
      if (t < 0) t = 0; else if (t > 1) t = 1;
      const projX = a.x + ex * t, projZ = a.z + ez * t;
      const dx = x - projX, dz = z - projZ;
      const dist = dx * dx + dz * dz;
      if (dist < bestDist) {
        bestDist = dist;
        // spojita arc-length: cum na zacatku segmentu + frakce * delka segmentu
        const segLen = cum[segIdx + 1] - cum[segIdx];
        bestS = cum[segIdx] + t * segLen;
        // lateral = znamenkova kolma vzdalenost od osy segmentu (vpravo = kladne, jako normalAt)
        const segLenL = Math.sqrt(segLen2);
        const nx = ez / segLenL, nz = -ex / segLenL;
        bestLateral = dx * nx + dz * nz;
      }
    }
    return { idx: bi, s: bestS, lateral: bestLateral };
  }

  // === VIZUAL ===
  buildVisual(world, scene, pts, meshes, bodies, biome, E, normalAt);
  const scenery = buildScenery(scene, { pts, pointAtS, normalAt, totalLength, groundY: E }, biome);
  const idealLine = buildIdealLine(scene, pts, normalAt, E);
  idealLine.visible = false;
  meshes.push(idealLine);

  return {
    id: def.id,
    name: def.name,
    setIdealLine(v) { idealLine.visible = !!v; },
    // Skry/zobraz scenerii (stromy, tribuny, kopce) - showcase auta v garazi/menu chce
    // cisty backdrop (jinak strom u stredu sceny zakryva auto). Pri zavode zpet viditelne.
    setSceneryVisible(v) { for (const o of scenery.objects) o.visible = !!v; },
    spawn: gridPositions[0].pos,
    heading: gridPositions[0].heading,
    gridPositions,
    checkpoints,
    numCheckpoints,
    totalLength,
    laps: LAPS,
    centerline: pts,
    samples: SAMPLES,
    pointAtS,
    nearest,
    surfaceGripAt(x, z) {
      const lat = Math.abs(nearest(x, z).lateral);
      if (lat <= ROAD_W / 2) return 1.0; // asfalt
      if (lat <= ROAD_W / 2 + 1.3) return 0.9; // kerb
      return 0.45; // trava/mimo trat
    },
    tangentAt,
    normalAt,
    groundY: E, // vyska terenu (kopce) pro spawn/reset
    dispose() {
      for (const m of meshes) {
        scene.remove(m);
        if (m.geometry) m.geometry.dispose();
        if (m.material) (Array.isArray(m.material) ? m.material : [m.material]).forEach((mt) => {
          // uvolni i textury materialu (napr. map: makeCheckerTexture() u startovni brany) -
          // jinak unika CanvasTexture per prepnuti trati. Zrcadli SceneManager.disposeObject.
          for (const k in mt) { const v = mt[k]; if (v && v.isTexture) v.dispose(); }
          mt.dispose();
        });
      }
      // odstran fyzikalni telesa (zem + mantinely) - jinak by se hromadila pri prepnuti trati
      for (const b of bodies) { try { world.removeRigidBody(b); } catch (e) { /* ignore */ } }
      scenery.dispose();
    },
  };
}

function buildVisual(world, scene, pts, meshes, bodies, biome, E, normAt) {
  const N = pts.length;

  // Zem = zvlneny teren (kopce). Vizualni mesh + trimesh collider ze STEJNYCH vrcholu
  // (collider presne odpovida tomu, co se vykresli - auto jezdi po kopcich).
  const TSIZE = 520, TRES = 104, thalf = TSIZE / 2, tstep = TSIZE / TRES;
  const tpos = [], tidx = [];
  for (let i = 0; i <= TRES; i++) {
    for (let j = 0; j <= TRES; j++) {
      const x = -thalf + j * tstep, z = -thalf + i * tstep;
      tpos.push(x, E(x, z), z);
    }
  }
  const tstride = TRES + 1;
  for (let i = 0; i < TRES; i++) {
    for (let j = 0; j < TRES; j++) {
      const a = i * tstride + j, b = a + 1, c = a + tstride, d = c + 1;
      tidx.push(a, c, b, b, c, d); // upward normals
    }
  }
  const groundGeo = new THREE.BufferGeometry();
  groundGeo.setAttribute('position', new THREE.Float32BufferAttribute(tpos, 3));
  groundGeo.setIndex(tidx);
  groundGeo.computeVertexNormals();
  const ground = new THREE.Mesh(groundGeo, new THREE.MeshStandardMaterial({ color: (biome && biome.ground) || 0x3f4a3a, roughness: 0.97 }));
  ground.receiveShadow = true;
  scene.add(ground); meshes.push(ground);
  const groundBody = world.createRigidBody(RAPIER.RigidBodyDesc.fixed());
  bodies.push(groundBody);
  world.createCollider(
    RAPIER.ColliderDesc.trimesh(new Float32Array(tpos), new Uint32Array(tidx)).setFriction(1.0).setRestitution(0),
    groundBody
  );

  // Silnice (ribbon) + okraje - vrcholy kopiruji teren (E) -> silnice na kopcich
  const half = ROAD_W / 2;
  const pos = [], idx = [];
  for (let i = 0; i <= N; i++) {
    const a = pts[i % N];
    const b = pts[(i + 1) % N];
    let tx = b.x - a.x, tz = b.z - a.z;
    const l = Math.hypot(tx, tz) || 1;
    const nx = tz / l, nz = -tx / l;
    const lx = a.x + nx * half, lz = a.z + nz * half;
    const rx = a.x - nx * half, rz = a.z - nz * half;
    pos.push(lx, E(lx, lz) + 0.04, lz);
    pos.push(rx, E(rx, rz) + 0.04, rz);
  }
  for (let i = 0; i < N; i++) {
    const k = i * 2;
    idx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
  }
  const roadGeo = new THREE.BufferGeometry();
  roadGeo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  roadGeo.setIndex(idx);
  roadGeo.computeVertexNormals();
  const road = new THREE.Mesh(roadGeo, new THREE.MeshStandardMaterial({ color: 0x202329, roughness: 0.9 }));
  road.receiveShadow = true;
  scene.add(road);
  meshes.push(road);

  // Cervenobile pruhovane obrubniky (kerbs) - vertex colors
  const RED = [0.82, 0.13, 0.13], WHITE = [0.92, 0.92, 0.92];
  for (const side of [1, -1]) {
    const kpos = [], kidx = [], kcol = [];
    const kerbW = 0.9;
    for (let i = 0; i <= N; i++) {
      const a = pts[i % N];
      const b = pts[(i + 1) % N];
      let tx = b.x - a.x, tz = b.z - a.z;
      const l = Math.hypot(tx, tz) || 1;
      const nx = tz / l, nz = -tx / l;
      const ix = a.x + side * nx * half, iz = a.z + side * nz * half;
      const ox = a.x + side * nx * (half + kerbW), oz = a.z + side * nz * (half + kerbW);
      kpos.push(ix, E(ix, iz) + 0.05, iz);
      kpos.push(ox, E(ox, oz) + 0.08, oz);
      const c = (i % 2 === 0) ? RED : WHITE;
      kcol.push(c[0], c[1], c[2], c[0], c[1], c[2]);
    }
    for (let i = 0; i < N; i++) {
      const k = i * 2;
      kidx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
    }
    const kgeo = new THREE.BufferGeometry();
    kgeo.setAttribute('position', new THREE.Float32BufferAttribute(kpos, 3));
    kgeo.setAttribute('color', new THREE.Float32BufferAttribute(kcol, 3));
    kgeo.setIndex(kidx);
    kgeo.computeVertexNormals();
    const kerb = new THREE.Mesh(kgeo, new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.55 }));
    kerb.receiveShadow = true;
    scene.add(kerb);
    meshes.push(kerb);
  }

  // Stredova carkovana cara (bila)
  const lpos = [], lidx = [];
  const lw = 0.2;
  for (let i = 0; i <= N; i++) {
    const a = pts[i % N];
    const b = pts[(i + 1) % N];
    let tx = b.x - a.x, tz = b.z - a.z;
    const l = Math.hypot(tx, tz) || 1;
    const nx = tz / l, nz = -tx / l;
    const lx = a.x + nx * lw, lz = a.z + nz * lw;
    const rx = a.x - nx * lw, rz = a.z - nz * lw;
    lpos.push(lx, E(lx, lz) + 0.06, lz);
    lpos.push(rx, E(rx, rz) + 0.06, rz);
  }
  for (let i = 0; i < N; i++) {
    if (i % 3 === 0) continue; // carkovani
    const k = i * 2;
    lidx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2);
  }
  const lgeo = new THREE.BufferGeometry();
  lgeo.setAttribute('position', new THREE.Float32BufferAttribute(lpos, 3));
  lgeo.setIndex(lidx);
  lgeo.computeVertexNormals();
  const cline = new THREE.Mesh(lgeo, new THREE.MeshStandardMaterial({ color: 0xe8e8e8, roughness: 0.6 }));
  scene.add(cline);
  meshes.push(cline);

  // normala v samplu (z okolnich bodu) - pouziva se top-level normalAt (predano jako normAt),
  // aby start/brana/mantinely sdilely STEJNOU normalu jako checkpointy/pointAtS/nearest.

  // --- Start/cil + brana (orientovane dle tangenty trati v bode 0) ---
  const s0 = pts[0], n0 = normAt(0);
  const sy = E(s0.x, s0.z); // vyska terenu na startu
  const gateYaw = Math.atan2(n0.x, n0.z);
  const start = new THREE.Mesh(
    new THREE.BoxGeometry(1.2, 0.02, ROAD_W),
    new THREE.MeshStandardMaterial({ color: 0xf2f2f7, roughness: 0.6 })
  );
  start.position.set(s0.x, sy + 0.08, s0.z);
  start.rotation.y = gateYaw;
  scene.add(start); meshes.push(start);

  const postMat = new THREE.MeshStandardMaterial({ color: 0xb8bcc4, metalness: 0.9, roughness: 0.4 });
  for (const sx of [-1, 1]) {
    const px = s0.x + n0.x * sx * (half + 0.6), pz = s0.z + n0.z * sx * (half + 0.6);
    const post = new THREE.Mesh(new THREE.BoxGeometry(0.4, 5.4, 0.4), postMat);
    post.position.set(px, E(px, pz) + 2.7, pz);
    post.castShadow = true;
    scene.add(post); meshes.push(post);
  }
  const banner = new THREE.Mesh(
    new THREE.BoxGeometry(0.2, 1.2, ROAD_W + 1.6),
    new THREE.MeshStandardMaterial({ map: makeCheckerTexture(), roughness: 0.5 })
  );
  banner.position.set(s0.x, sy + 5.1, s0.z);
  banner.rotation.y = gateYaw;
  banner.castShadow = true;
  scene.add(banner); meshes.push(banner);

  // --- Mantinely podel trati (vizualni stena + hrube orientovane collidery) ---
  const BARRIER_OFF = half + 9; // runoff (trava) pak stena
  const wallH = 1.15;
  const wallMat = new THREE.MeshStandardMaterial({ color: 0xc9ccd2, roughness: 0.65, metalness: 0.1, side: THREE.DoubleSide });
  for (const side of [1, -1]) {
    // vizualni svisla stuha
    const wpos = [], widx = [];
    for (let i = 0; i <= N; i++) {
      const p = pts[i % N], n = normAt(i % N);
      const ox = p.x + side * n.x * BARRIER_OFF, oz = p.z + side * n.z * BARRIER_OFF;
      const ey = E(ox, oz);
      wpos.push(ox, ey, oz, ox, ey + wallH, oz);
    }
    for (let i = 0; i < N; i++) { const k = i * 2; widx.push(k, k + 1, k + 2, k + 1, k + 3, k + 2); }
    const wgeo = new THREE.BufferGeometry();
    wgeo.setAttribute('position', new THREE.Float32BufferAttribute(wpos, 3));
    wgeo.setIndex(widx); wgeo.computeVertexNormals();
    const wm = new THREE.Mesh(wgeo, wallMat);
    wm.receiveShadow = true;
    scene.add(wm); meshes.push(wm);
    // hrube collidery (kazdych BSTEP samplu)
    const BSTEP = 8;
    for (let i = 0; i < N; i += BSTEP) {
      const i2 = (i + BSTEP) % N;
      const p1 = pts[i], np1 = normAt(i), p2 = pts[i2], np2 = normAt(i2);
      const ax = p1.x + side * np1.x * BARRIER_OFF, az = p1.z + side * np1.z * BARRIER_OFF;
      const bx = p2.x + side * np2.x * BARRIER_OFF, bz = p2.z + side * np2.z * BARRIER_OFF;
      const mx = (ax + bx) / 2, mz = (az + bz) / 2;
      const dx = bx - ax, dz = bz - az; const len = Math.hypot(dx, dz) || 1; const yaw = Math.atan2(dx, dz);
      const body = world.createRigidBody(RAPIER.RigidBodyDesc.fixed()
        .setTranslation(mx, E(mx, mz) + 0.6, mz)
        .setRotation({ x: 0, y: Math.sin(yaw / 2), z: 0, w: Math.cos(yaw / 2) }));
      world.createCollider(RAPIER.ColliderDesc.cuboid(0.3, 0.6, len / 2 + 0.4).setRestitution(0).setFriction(0.5), body);
      bodies.push(body);
    }
  }
}

// Ideální stopa - svitici stuha podel centerline (driving aid, default skryta)
function buildIdealLine(scene, pts, normalAt, E) {
  const N = pts.length;
  const HALF = 0.38;
  const positions = new Float32Array((N + 1) * 2 * 3);
  for (let i = 0; i <= N; i++) {
    const p = pts[i % N];
    const n = normalAt(i % N);
    const lx = p.x + n.x * HALF, lz = p.z + n.z * HALF;
    const rx = p.x - n.x * HALF, rz = p.z - n.z * HALF;
    const o = i * 6;
    positions[o] = lx; positions[o + 1] = E(lx, lz) + 0.08; positions[o + 2] = lz;
    positions[o + 3] = rx; positions[o + 4] = E(rx, rz) + 0.08; positions[o + 5] = rz;
  }
  const idx = [];
  for (let i = 0; i < N; i++) {
    const a = i * 2, b = i * 2 + 1, c = (i + 1) * 2, d = (i + 1) * 2 + 1;
    idx.push(a, c, b, b, c, d);
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geo.setIndex(idx);
  const mat = new THREE.MeshBasicMaterial({
    color: 0x33e2ff, transparent: true, opacity: 0.45, depthWrite: false,
  });
  const mesh = new THREE.Mesh(geo, mat);
  mesh.renderOrder = 2;
  scene.add(mesh);
  return mesh;
}

// Sachovnicova textura (canvas) pro startovni branu
function makeCheckerTexture(squaresX = 28, squaresY = 3) {
  const c = document.createElement('canvas');
  c.width = squaresX * 8;
  c.height = squaresY * 8;
  const ctx = c.getContext('2d');
  const sw = c.width / squaresX, sh = c.height / squaresY;
  for (let y = 0; y < squaresY; y++) {
    for (let x = 0; x < squaresX; x++) {
      ctx.fillStyle = (x + y) % 2 ? '#141414' : '#eeeeee';
      ctx.fillRect(x * sw, y * sh, sw, sh);
    }
  }
  const tex = new THREE.CanvasTexture(c);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}
